'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { type Locale, dictionaries, type Dictionary } from './dictionaries';

interface I18nContextType {
  locale: Locale;
  setLocale: (newLocale: Locale) => void;
  t: Dictionary;
  isRtl: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const LOCALE_STORAGE_KEY = 'app_preferred_locale';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>('en'); // Default Root Language is English

  const applyDocumentDirection = (loc: Locale) => {
    const dir = dictionaries[loc].dir;
    const root = document.documentElement;
    root.dir = dir;
    root.lang = loc;

    // Apply exact typography class optimization per language requirement
    if (loc === 'fa' || loc === 'ar') {
      root.classList.add('font-[var(--font-vazirmatn)]');
      root.classList.remove('font-[var(--font-inter)]', 'font-[var(--font-dm-sans)]');
    } else {
      root.classList.add('font-[var(--font-inter)]');
      root.classList.remove('font-[var(--font-vazirmatn)]');
    }
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY) as Locale | null;
      if (stored && dictionaries[stored]) {
        setTimeout(() => setLocaleState(stored), 0);
        applyDocumentDirection(stored);
      } else {
        applyDocumentDirection('en');
      }
    } catch (e) {
      applyDocumentDirection('en');
    }
  }, []);



  const setLocale = (newLocale: Locale) => {
    if (!dictionaries[newLocale]) return;
    setLocaleState(newLocale);
    applyDocumentDirection(newLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
      document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (e) {}
  };

  const currentDict = dictionaries[locale] || dictionaries.en;
  const isRtl = currentDict.dir === 'rtl';

  return (
    <I18nContext.Provider value={{ locale, setLocale, t: currentDict, isRtl }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useLocale = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    // Return default English fallback if used outside provider during SSR
    return {
      locale: 'en',
      setLocale: () => {},
      t: dictionaries.en,
      isRtl: false,
    };
  }
  return context;
};
